import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { onIdTokenChanged, type User } from 'firebase/auth'
import { initFirebase } from './firebase'
import { meApi } from '../api/endpoints'
import { hydrateSourcingEnrolled } from './sourcing'
import type { UserClaims } from '../api/types'

/**
 * lib/identity.ts — ONE answer to "who is this, and what may they see".
 *
 * ── WHAT IT REPLACES ──────────────────────────────────────────────────────
 * Seven separate `getIdTokenResult().claims.role` reads, each with its own
 * `watchAuthState` subscription, its own catch, and its own idea of the
 * default: `Header.tsx` (unknown → 'buyer'), `routes/Landing.tsx`
 * (`|| 'buyer'`), `routes/Admin.tsx` (`role==='admin' || isAdmin`),
 * `routes/Chat.tsx`, `components/Gemma.tsx`, `mobile/screens/ProfileScreen.tsx`,
 * `mobile/screens/gemma/useGemmaChat.ts`. Seven copies of an authorization
 * decision is seven places for it to drift.
 *
 * ── AND THE REAL BUG: NOTHING WAS SUBSCRIBED TO THE TOKEN ─────────────────
 * All seven used `onAuthStateChanged`, which fires on sign-in and sign-out and
 * NOT on a claim change. So an admin promoting someone (`POST
 * /admin/set-role`, which sets a custom claim) or an org membership landing
 * was invisible in the UI until the user signed out and back in — and support
 * has genuinely told people to do that. `onIdTokenChanged` fires on every
 * token refresh, so a claim change now propagates on its own, and
 * `refreshIdentity()` forces it immediately for the case where the app itself
 * caused the change.
 *
 * ── STRUCTURE: TWO LAYERS, ON PURPOSE ─────────────────────────────────────
 * `useIdentity()`  — token-only. Synchronous, zero network, never suspends.
 *                    This is what a nav bar or a route guard needs.
 * `useIdentityProfile()` — `/me`, fetched once per uid and shared. This is
 *                    where `kycApproved`, `accountType` and `org` live.
 *
 * They are separate because ORG IDENTITY IS NOT IN THE TOKEN AND MUST NOT BE.
 * `functions/src/shared/identity.ts` is explicit: "NO NEW CUSTOM CLAIMS —
 * orgId / orgRole / seat entitlement resolve from Firestore." A claim is a
 * cached copy with a refresh window; seat suspension and KYC revocation have
 * to be able to take effect NOW. Anything that gates data reads it from the
 * server per request; this module never becomes the authority for that.
 *
 * ── IT IS NOT AN AUTHORIZATION BOUNDARY ───────────────────────────────────
 * Custom claims are client-readable. Everything here is for RENDERING —
 * which nav items to show, which empty state to draw. Every price, supplier
 * contact and `refStockId` is gated server-side by `applyMarketplace`
 * (CLAUDE.md invariant 3) and stays that way.
 *
 * ── NO DEAD ENDS ──────────────────────────────────────────────────────────
 * Every failure resolves to a RENDERABLE state, never a throw and never a
 * permanent spinner: no Firebase config → `unavailable` (treated as signed
 * out); a `/me` that 401s or times out → `error` with `reload()`, with the
 * token identity still intact so the shell keeps working and the user can act.
 */

export type PlatformRole = 'admin' | 'supplier' | 'buyer'

export interface Identity {
  /**
   * `loading` until the first auth tick. `unavailable` means Firebase never
   * initialised (missing config) — render as signed-out, never as a spinner.
   */
  status: 'loading' | 'anon' | 'signedIn' | 'unavailable'
  signedIn: boolean
  uid: string | null
  email: string | null
  emailVerified: boolean
  /**
   * The platform role claim. `null` when signed out. A signed-in user whose
   * claim is missing or unrecognised is a BUYER — self-registered buyers have
   * a uid and no role claim at all, and CLAUDE.md invariant 1 says they must
   * count as fully signed in or the marketplace breaks for them.
   */
  role: PlatformRole | null
  isAdmin: boolean
  isSupplier: boolean
  /** Moves only on a real re-authentication. Used by the device claim. */
  authTime: string | null
}

const ANON: Identity = {
  status: 'anon', signedIn: false, uid: null, email: null, emailVerified: false,
  role: null, isAdmin: false, isSupplier: false, authTime: null,
}

let current: Identity = { ...ANON, status: 'loading' }
const subs = new Set<() => void>()
let started = false

function emit(next: Identity): void {
  // Reference equality is the useSyncExternalStore contract — only publish a
  // new object when something actually changed, or every token refresh (hourly)
  // re-renders every consumer for nothing.
  if (
    next.status === current.status && next.uid === current.uid && next.role === current.role &&
    next.email === current.email && next.emailVerified === current.emailVerified &&
    next.isAdmin === current.isAdmin && next.authTime === current.authTime
  ) return
  current = next
  subs.forEach((f) => { try { f() } catch { /* one bad subscriber must not stop the rest */ } })
}

function normalizeRole(v: unknown): PlatformRole {
  return v === 'admin' || v === 'supplier' ? v : 'buyer'
}

async function readUser(user: User): Promise<Identity> {
  let role: PlatformRole = 'buyer'
  let isAdmin = false
  let authTime: string | null = null
  try {
    // Cached — no network round-trip unless the token is expiring.
    const tok = await user.getIdTokenResult()
    const claims = tok.claims as { role?: unknown; isAdmin?: unknown }
    role = normalizeRole(claims.role)
    // `Admin.tsx` has always honoured a bare `isAdmin` claim as well as
    // `role:'admin'`; keeping both here is what makes it safe to delete that
    // bespoke check later.
    isAdmin = role === 'admin' || claims.isAdmin === true
    authTime = tok.authTime ?? null
  } catch {
    // A token read failure must not log anybody out of the UI. Degrade to the
    // least-privileged signed-in identity.
  }
  return {
    status: 'signedIn',
    signedIn: true,
    uid: user.uid,
    email: user.email ?? null,
    emailVerified: user.emailVerified,
    role,
    isAdmin,
    isSupplier: role === 'supplier',
    authTime,
  }
}

/** Idempotent, lazy: the subscription starts the first time anyone asks. */
function start(): void {
  if (started) return
  started = true
  // ONE try around both halves. `initFirebase()` throwing (missing config) is
  // the obvious failure, but `onIdTokenChanged` can throw too — on a partially
  // stubbed auth object, and on any future SDK that validates its argument.
  // This function runs from `useSyncExternalStore`'s getSnapshot, so a throw
  // here is a render-phase throw: it takes the whole shell down rather than
  // degrading one nav item. `unavailable` renders as signed-out, which is the
  // safe direction.
  try {
    const { auth } = initFirebase()
    onIdTokenChanged(auth, (user) => {
      if (!user) {
        emit(ANON)
        resetProfile()
        return
      }
      void readUser(user).then(emit)
    })
  } catch {
    emit({ ...ANON, status: 'unavailable' })
  }
}

export function getIdentity(): Identity {
  start()
  return current
}

export function subscribeIdentity(cb: () => void): () => void {
  start()
  subs.add(cb)
  return () => { subs.delete(cb) }
}

/** Token identity. Synchronous, no network, safe to call from anywhere. */
export function useIdentity(): Identity {
  return useSyncExternalStore(subscribeIdentity, getIdentity, () => current)
}

/**
 * Force a token refresh so a claim the app just caused (an admin promotion, a
 * role grant) lands without a sign-out. `onIdTokenChanged` delivers the new
 * claims, so the return value is a convenience, not the channel.
 */
export async function refreshIdentity(): Promise<Identity> {
  try {
    const { auth } = initFirebase()
    const user = auth.currentUser
    if (!user) return current
    await user.getIdToken(true)
    emit(await readUser(user))
  } catch { /* offline / revoked — keep the last good identity, never throw */ }
  return current
}

// ─────────────────────────────────────────────────────────────────────────
// Profile layer — `/me`, one fetch per uid, shared by every consumer.
// ─────────────────────────────────────────────────────────────────────────

export interface IdentityProfile {
  status: 'idle' | 'loading' | 'ready' | 'error'
  /** The server's view of this person. Null until loaded. */
  user: UserClaims | null
  /** The org this person belongs to, when orgs are live. Null otherwise. */
  org: NonNullable<UserClaims['org']> | null
  orgRole: 'owner' | 'admin' | 'member' | null
  /**
   * May this caller see prices / supplier contacts? Mirrors the server rule
   * (invariant 3) for RENDERING only — the server still decides per request.
   */
  verified: boolean
  error: string | null
}

const EMPTY_PROFILE: IdentityProfile = {
  status: 'idle', user: null, org: null, orgRole: null, verified: false, error: null,
}

let profile: IdentityProfile = EMPTY_PROFILE
let profileUid: string | null = null
const profileSubs = new Set<() => void>()

function emitProfile(next: IdentityProfile): void {
  profile = next
  profileSubs.forEach((f) => { try { f() } catch { /* ignore */ } })
}

/**
 * Drop the cached profile. Called automatically when the token goes away, so
 * one person's org, KYC state and company name can never be read by the next
 * person to sign in on a shared workstation.
 */
export function resetProfile(): void {
  profileUid = null
  emitProfile(EMPTY_PROFILE)
  // Sign-out ⇒ no one is enrolled; hide every sourcing entry point until the next
  // /me says otherwise (so the ＋ can't linger for the next person on a shared box).
  hydrateSourcingEnrolled(false)
}

function shapeProfile(user: UserClaims): IdentityProfile {
  const org = user.org ?? null
  return {
    status: 'ready',
    user,
    org,
    orgRole: org?.orgRole ?? null,
    // Admins pass on any verified token, exactly as the server has it.
    verified: user.role === 'admin' || user.kycApproved === true || org?.kycApproved === true,
    error: null,
  }
}

/**
 * Fetch `/me` for `uid`, at most once per uid unless `force`. Safe to call
 * from an effect on every mount — the second and tenth caller are no-ops.
 */
export function loadProfile(uid: string, force = false): void {
  // A previous FAILURE is not a cached answer — a later mount retries once, so
  // a user who navigated away and back is not stuck staring at an error they
  // can only clear by finding the retry button.
  if (!force && profileUid === uid && (profile.status === 'ready' || profile.status === 'loading')) return
  profileUid = uid
  emitProfile({ ...EMPTY_PROFILE, status: 'loading' })
  void meApi
    .get()
    .then((r) => {
      // A response that landed after the user changed belongs to nobody.
      if (profileUid !== uid) return
      emitProfile(shapeProfile(r.user))
      // Per-user agentic-sourcing visibility — the server's word gates the ＋.
      hydrateSourcingEnrolled(r.user.sourcingEnrolled)
    })
    .catch((e: unknown) => {
      if (profileUid !== uid) return
      emitProfile({
        ...EMPTY_PROFILE,
        status: 'error',
        error: e instanceof Error ? e.message : 'Could not load your account',
      })
    })
}

export function getProfile(): IdentityProfile { return profile }
export function subscribeProfile(cb: () => void): () => void {
  profileSubs.add(cb)
  return () => { profileSubs.delete(cb) }
}

/**
 * The server-side half of identity — `kycApproved`, `accountType`, `org`,
 * seats. Fetched once per uid and shared across every caller, so ten mounted
 * components cost one request.
 *
 * `reload()` is the EXIT: an org surface that failed to load must offer "try
 * again" rather than a blank panel or a sign-out.
 */
export function useIdentityProfile(): IdentityProfile & { reload: () => void } {
  const id = useIdentity()
  const snap = useSyncExternalStore(subscribeProfile, getProfile, () => profile)

  // In an effect, never in render: `loadProfile` publishes to the external
  // store, and publishing during render is a cross-component setState.
  useEffect(() => {
    if (id.signedIn && id.uid) loadProfile(id.uid)
    else if (id.status === 'anon' || id.status === 'unavailable') resetProfile()
  }, [id.signedIn, id.uid, id.status])

  const reload = useCallback(() => { if (id.uid) loadProfile(id.uid, true) }, [id.uid])
  return { ...snap, reload }
}
